// Cobertura de cartolas: qué días tienen cartola, huecos y continuidad de saldos (Iter 352)
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const CLP = (v) => (v || 0).toLocaleString('es-CL');
const ddmm = (f) => fmtFecha(f);
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const DIA_CLS = {
  mov: 'bg-emerald-500', ok: 'bg-slate-200', falta: 'bg-red-500', pendiente: 'bg-amber-300', quiebre: 'bg-orange-500',
};
const DIA_LABEL = {
  mov: 'Con movimientos', ok: 'Sin movimientos (saldo continuo)', falta: 'FALTA cartola (saldo no cuadra)',
  pendiente: 'Sin cartola aún', quiebre: 'Quiebre de saldo dentro del día',
};

function GrillaMeses({ dias }) {
  const meses = {};
  Object.entries(dias).forEach(([f, est]) => {
    const mes = f.slice(0, 7);
    (meses[mes] = meses[mes] || {})[Number(f.slice(8, 10))] = est;
  });
  return (
    <div className="space-y-1.5" data-testid="cobertura-grid">
      {Object.keys(meses).sort().map((mes) => {
        const [y, m] = mes.split('-').map(Number);
        const nDias = new Date(y, m, 0).getDate();
        return (
          <div key={mes} className="flex items-center gap-2">
            <span className="w-16 text-xs text-muted-foreground">{MESES[m - 1]} {y}</span>
            <div className="flex gap-0.5">
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                const est = d <= nDias ? meses[mes][d] : null;
                return (
                  <div key={d} title={est ? `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}: ${DIA_LABEL[est]}` : ''}
                    className={`h-4 w-4 rounded-sm ${est ? DIA_CLS[est] : 'bg-transparent'} ${d > nDias ? 'invisible' : ''}`}
                    data-testid={est ? `dia-${mes}-${String(d).padStart(2, '0')}-${est}` : undefined} />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CuentaCobertura({ c }) {
  const alertaSinCartola = (c.dias_desde_ultimo_mov ?? 0) > 10;
  return (
    <Card data-testid={`cobertura-cuenta-${c.cuenta}`}>
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-semibold">Cuenta {c.cuenta}</p>
            <p className="text-xs text-muted-foreground">{c.titular} · {ddmm(c.fecha_desde)} → {ddmm(c.fecha_hasta)} · {c.total_movs} movimientos en {c.dias_con_movs} días</p>
          </div>
          {c.huecos_falta === 0 && c.quiebres.length === 0
            ? <Badge className="bg-emerald-100 text-emerald-700" data-testid="badge-continuidad-ok"><CheckCircle2 className="mr-1 h-3 w-3" /> Saldos continuos</Badge>
            : <Badge className="bg-red-100 text-red-700" data-testid="badge-continuidad-error"><AlertTriangle className="mr-1 h-3 w-3" /> Revisar continuidad</Badge>}
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ['Saldo al ' + ddmm(c.fecha_hasta), `$${CLP(c.saldo_final)}`, '', 'saldo-final'],
            ['Última cartola', c.dias_desde_ultimo_mov == null ? '—' : `hace ${c.dias_desde_ultimo_mov} días`, alertaSinCartola ? 'text-amber-600' : '', 'dias-sin-cartola'],
            ['Huecos con faltantes', c.huecos_falta, c.huecos_falta ? 'text-red-600' : 'text-emerald-600', 'huecos-falta'],
            ['Quiebres de saldo', c.quiebres.length, c.quiebres.length ? 'text-orange-600' : 'text-emerald-600', 'quiebres'],
          ].map(([label, val, cls, tid]) => (
            <div key={label} className="rounded-md border p-2.5" data-testid={`card-${tid}`}>
              <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
              <p className={`text-base font-bold ${cls}`}>{val}</p>
            </div>
          ))}
        </div>

        <GrillaMeses dias={c.dias} />
        <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
          {Object.entries(DIA_LABEL).map(([k, l]) => (
            <span key={k} className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-sm ${DIA_CLS[k]}`} />{l}</span>
          ))}
        </div>

        {(c.huecos.some((h) => h.estado === 'falta') || c.quiebres.length > 0) && (
          <div className="space-y-1 rounded-md border border-red-200 bg-red-50 p-3 text-sm" data-testid="cobertura-alertas">
            {c.huecos.filter((h) => h.estado === 'falta').map((h) => (
              <p key={h.desde}>• Falta cartola del <b>{ddmm(h.desde)}</b> al <b>{ddmm(h.hasta)}</b> ({h.dias} días): el saldo esperado era ${CLP(h.saldo_esperado)} y la siguiente cartola parte en ${CLP(h.saldo_real)} (diferencia ${CLP(h.diferencia)}).</p>
            ))}
            {c.quiebres.map((q) => (
              <p key={q.fecha}>• Quiebre el <b>{ddmm(q.fecha)}</b>: {q.encadenados} de {q.movs_dia} movimientos encadenan; diferencia ${CLP(q.diferencia)} (¿movimientos faltantes o cartola parcial de ese día?).</p>
            ))}
          </div>
        )}

        <table className="w-full text-xs" data-testid="cobertura-cargas-table">
          <thead><tr className="border-b text-left text-muted-foreground">
            <th className="py-1.5 pr-2">Archivo</th><th className="py-1.5 pr-2">Período</th><th className="py-1.5 pr-2 text-right">Movs</th>
            <th className="py-1.5 pr-2 text-right">Saldo inicial</th><th className="py-1.5 pr-2 text-right">Saldo final</th><th className="py-1.5">Subida</th>
          </tr></thead>
          <tbody>
            {c.cargas.map((g) => (
              <tr key={g.id} className="border-b last:border-0" data-testid={`carga-row-${g.id}`}>
                <td className="py-1.5 pr-2">{g.nombre_archivo}</td>
                <td className="py-1.5 pr-2 whitespace-nowrap">{ddmm(g.fecha_desde)} → {ddmm(g.fecha_hasta)}</td>
                <td className="py-1.5 pr-2 text-right">{g.nuevos}{g.duplicados ? <span className="text-muted-foreground"> (+{g.duplicados} dup)</span> : null}</td>
                <td className="py-1.5 pr-2 text-right">${CLP(g.saldo_inicial)}</td>
                <td className="py-1.5 pr-2 text-right">${CLP(g.saldo_final)}</td>
                <td className="py-1.5 whitespace-nowrap">{fmtFecha(g.fecha_carga)} · {g.cargado_por}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export function CoberturaTab({ refreshKey, cuenta = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/conciliacion-bancaria/cobertura', { params: cuenta ? { cuenta } : {} })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Error cargando cobertura'))
      .finally(() => setLoading(false));
  }, [refreshKey, cuenta]);

  if (loading) return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground" data-testid="cobertura-loading"><Loader2 className="h-4 w-4 animate-spin" /> Calculando cobertura...</div>;
  if (!data?.cuentas?.length) return <p className="p-6 text-sm text-muted-foreground" data-testid="cobertura-empty">Sin cartolas cargadas todavía.</p>;
  return <div className="space-y-4" data-testid="cobertura-tab">{data.cuentas.map((c) => <CuentaCobertura key={c.cuenta} c={c} />)}</div>;
}
